<script lang="ts">
  import { push } from "svelte-spa-router";
  import { scaleLinear } from "d3-scale";
  import { max } from "d3-array";
  import TextNew from "../TextNew.svelte";
  import type { ChatSpendBreakdown } from "../../api/spend-report";
  import { spendTooltip } from "../../actions/tooltip";
  import type { SpendBreakdownFilterType } from "../../helpers/types";
  import Skeleton from "../skeletons/Skeleton.svelte";
  import { CHAT_FILTER_PARAM_KEY } from "../invoices/constants";
  import { round } from "../../helpers/math";
  import { currencyFormatter } from "../../helpers/localise";

  type SupplierCategoryDisplay = Record<string, { label: string; color: string }>;

  const visualsBySupplierCategory: SupplierCategoryDisplay = {
    meat: { label: "meat", color: "#F35B05" },
    alcohol: { label: "alcohol", color: "#8590A2" },
    drinks: { label: "soft drinks", color: "#6146AF" },
    supplies: { label: "packaging & hygiene", color: "#B6C2CF" },
    dairy: { label: "dairy & cheese", color: "#F6B802" },
    fruit_veg: { label: "fruit & veg", color: "#18936F" },
    specialty: { label: "specialty", color: "#7678ED" },
    coffee_tea: { label: "coffee & tea", color: "#A97E54" },
    general: { label: "dry store", color: "#88D498" },
    bakery: { label: "baked goods", color: "#F18702" },
    fish_seafood: { label: "fish", color: "#4389FA" },
    unknown: { label: "", color: "#7678ED" },
  };

  export let breakdowns: ChatSpendBreakdown[] | undefined = undefined;
  export let breakdownFilterType: SpendBreakdownFilterType;
  let currentHoveredChatID: string | null = null;

  $: maxTotal = max(breakdowns ?? [], (d) =>
    breakdownFilterType === "net" ? d.total_net : d.total_gross
  );

  $: xScale = scaleLinear()
    .domain([0, maxTotal ?? 0])
    .range([0, 100]);

  $: highlightedField =
    breakdownFilterType === "gross" ? ("totalGross" as const) : ("totalNet" as const);

  const skeletonBarWidthPercentages = ["87%", "72%", "52%", "40%", "28%", "19%"];

  function clearHoveredSupplierChat() {
    currentHoveredChatID = null;
  }
</script>

<div class="grid" class:empty={!breakdowns?.length}>
  {#if breakdowns?.length}
    {#each breakdowns as b (b.chat_id)}
      <div
        class="left-label"
        class:unhovered={currentHoveredChatID === null ? false : currentHoveredChatID !== b.chat_id}
      >
        <TextNew
          size="s"
          --rk-text-color={currentHoveredChatID != null && currentHoveredChatID !== b.chat_id
            ? "var(--rk-text-low)"
            : undefined}
          overflow="ellipsis">{b.supplier_name}</TextNew
        >
        <TextNew size="xs" --rk-text-color="var(--rk-text-low)">
          {visualsBySupplierCategory[b.supplier_category]?.label ?? b.supplier_category}
        </TextNew>
      </div>
      <div
        class="bar-container"
        on:mouseenter={() => {
          currentHoveredChatID = b.chat_id;
        }}
        on:mouseleave={clearHoveredSupplierChat}
        use:spendTooltip={{
          totalGross: b.total_gross,
          totalNet: b.total_net,
          totalVAT: b.total_vat,
          creditNoteNet: b.net_credit_note,
          creditNoteVAT: b.credit_note_vat,
          invoiceNet: b.net_invoice,
          invoiceVAT: b.invoice_vat,
          accountNumber: b.account_number,
          highlightedField,
        }}
        class:unhovered={currentHoveredChatID === null ? false : currentHoveredChatID !== b.chat_id}
      >
        <button
          class="bar"
          style:width={`${round(
            xScale(breakdownFilterType === "net" ? b.total_net : b.total_gross),
            2
          )}%`}
          on:click={() => {
            const queryParams = new URLSearchParams({
              [CHAT_FILTER_PARAM_KEY]: b.chat_id,
              entry: "overview",
            });
            push(`/report?${queryParams.toString()}`);
          }}
          style:background-color={visualsBySupplierCategory[b.supplier_category]?.color ??
            visualsBySupplierCategory.unknown.color}
        />
        <div>
          <TextNew size="s">
            {$currencyFormatter.format(
              (breakdownFilterType === "net" ? b.total_net : b.total_gross) / 100
            )}
          </TextNew>
          <span class="right-label-caption">
            {breakdownFilterType === "net" ? b.proportion_net : b.proportion_gross}%
          </span>
        </div>
      </div>
    {/each}
  {:else}
    {#each skeletonBarWidthPercentages as skWidth}
      <div class="label-skeleton skeleton-gap">
        <Skeleton --skeleton-height="12px" --skeleton-width="128px" />
        <Skeleton --skeleton-height="12px" --skeleton-width="70px" />
      </div>
      <div class="skeleton-gap">
        <Skeleton
          --skeleton-height="40px"
          --skeleton-width={skWidth}
          --skeleton-radius="var(--rk-rounding-s)"
        />
      </div>
    {/each}
  {/if}
</div>

<style>
  .left-label.unhovered {
    opacity: 0.5;
  }
  .left-label {
    margin-top: 4px;
    max-width: 200px;
  }

  .label-skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .right-label-caption {
    display: block;
    font-size: 12px;
    line-height: var(--rk-line-height-xs);
    letter-spacing: -0.5%;
    color: var(--rk-text-low);
  }

  .bar-container.unhovered {
    opacity: 0.4;
  }

  .bar-container {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 10px;
    cursor: pointer;
    padding-bottom: 20px;
  }

  .skeleton-gap {
    margin-bottom: 20px;
  }

  .bar-container:last-child {
    padding-bottom: 0;
  }

  .bar {
    height: 40px;
    min-width: 3px;
    border-radius: 8px;
    background-color: #f0f0f0;
    flex: 0 1 auto;
  }

  .grid {
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-template-rows: min-content;
    column-gap: 12px;
    grid-auto-rows: min-content;
    border-bottom: 0;
  }

  .empty {
    min-height: 21.6vh;
  }
</style>
